import { z } from 'zod';
import { CalendarDay, CalendarTime, getCurrencySymbol } from '../../../utils';
import { Country } from '../../../labels';
import { isMongoId } from '../../helpers';
import {
  CrsClassification,
  FatcaClassification,
  FatcaExemptBeneficialOwner,
} from 'further-types/lp/investor-tax-details';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { LegalEntityType } from 'further-types/legal-entity';
import { KycRequest } from 'further-types/lp/kyc';
import { KybRequest } from 'further-types/lp/kyb';

export const SaveIndividualInvestorDetailsInput = z.object({
  firstName: z.string().min(1, { message: 'First name is required.' }),
  middleName: z.string().optional(),
  lastName: z.string().min(1, { message: 'Last name is required.' }),
  dateOfBirth: z.instanceof(CalendarDay).optional(),
  nationalities: z.array(z.nativeEnum(Country)),
  phoneNumber: z.string().optional(),
  addresses: z.array(
    z.object({
      address1: z.string().optional(),
      address2: z.string().optional(),
      city: z.string().optional(),
      postcode: z.string().optional(),
      country: z.nativeEnum(Country).optional().nullable(),
      state: z.string().optional(),
      hasBeenUsedForThreeYearsOrMore: z.boolean().optional(),
    }),
  ),
  jobTitle: z.string().optional(),
  sector: z.string().optional(),
});

export const SaveLegalEntityDetailsInput = z.object({
  registeredName: z
    .string()
    .min(1, { message: 'Registered name is required.' }),
  registrationNumber: z.string().optional(),
  registrationDate: z.instanceof(CalendarDay).optional(),
  legalEntityType: z.nativeEnum(LegalEntityType).optional(),
  registrationCountry: z.nativeEnum(Country).optional(),
  registrationState: z.string().optional(),
  keyContactPhoneNumber: z.string().optional(),
  addresses: z.array(
    z.object({
      address1: z.string().optional(),
      address2: z.string().optional(),
      city: z.string().optional(),
      postcode: z.string().optional(),
      country: z.nativeEnum(Country).optional().nullable(),
      state: z.string().optional(),
      hasBeenUsedForThreeYearsOrMore: z.boolean().optional(),
    }),
  ),
});

export const SaveBankAccountDetailsInput = z.object({
  bankName: z.string().optional(),
  accountHolderName: z.string().optional(),
  bankAddress: z.string().optional(),
  bankAccountNumber: z.string().optional(),
  bankSortCode: z.string().optional(),
  swift: z.string().optional(),
  iban: z.string().optional(),
  additionalBankNumberOrCode: z.string().optional(),
});

export const ConfirmInvestorDetailsInput = z.object({
  vehicleApplicationId: z.string().refine(isMongoId()),
});

export const SaveTaxDetailsInput = z.object({
  vehicleApplicationId: z.string().refine(isMongoId()),
  principalAddress: z
    .object({
      address1: z.string().min(1, { message: 'Address line 1 is required.' }),
      address2: z.string().optional(),
      city: z.string().min(1, { message: 'City is required.' }),
      postcode: z.string().min(1, { message: 'Postcode is required.' }),
      country: z.nativeEnum(Country),
      state: z.string().optional(),
    })
    .optional(),
  taxResidencies: z
    .array(
      z.object({
        country: z.nativeEnum(Country),
        state: z.string().optional(),
        taxIdentificationNumber: z
          .string()
          .min(1, { message: 'Tax identification number is required.' }),
      }),
    )
    .min(1, { message: 'At least one tax residency is required.' }),
  fatcaClassification: z.object({
    classification: z.nativeEnum(FatcaClassification),
    tin: z.string().optional(),
    giin: z.string().optional(),
    sponsor: z.string().optional(),
    exemptionDetails: z.string().optional(),
    exemptBeneficialOwnerType: z
      .nativeEnum(FatcaExemptBeneficialOwner)
      .optional(),
    controllingPersonSelfCertificationFiles: z.array(
      z.object({
        path: z.string().min(1, { message: 'File path is required.' }),
        name: z.string().min(1, { message: 'File name is required.' }),
      }),
    ),
    formerUsCitizenFiles: z.array(
      z.object({
        path: z.string().min(1, { message: 'File path is required.' }),
        name: z.string().min(1, { message: 'File name is required.' }),
      }),
    ),
  }),
  crsClassification: z.object({
    classification: z.nativeEnum(CrsClassification),
    nonReportingFiOtherDetail: z.string().optional(),
    controllingPersonSelfCertificationFiles: z.array(
      z.object({
        path: z.string().min(1, { message: 'File path is required.' }),
        name: z.string().min(1, { message: 'File name is required.' }),
      }),
    ),
  }),
  declaration: z
    .object({
      fullName: z.string().min(1, { message: 'Full name is required.' }),
      signature: z.string().min(1, { message: 'Signature is required.' }),
      date: z.instanceof(CalendarDay),
      capacity: z.string().min(1, { message: 'Capacity is required.' }),
      files: z.array(
        z.object({
          path: z.string().min(1, { message: 'File path is required.' }),
          name: z.string().min(1, { message: 'File name is required.' }),
        }),
      ),
    })
    .optional(),
  customRequestAnswers: z
    .array(
      z.object({
        onboardingCustomRequestId: z.string().refine(isMongoId()),
        requestTitle: z
          .string()
          .min(1, { message: 'Request title is required.' }),
        requestDescription: z
          .string()
          .min(1, { message: 'Request description is required.' }),
        answerText: z.string(),
        answerFiles: z.array(
          z.object({
            path: z.string().min(1, { message: 'File path is required.' }),
            name: z.string().min(1, { message: 'File name is required.' }),
          }),
        ),
      }),
    )
    .optional(),
});

export const SaveIndividualInvestorTaxClassificationDetails = z.object({
  vehicleApplicationId: z.string().refine(isMongoId()),
  principalAddress: z
    .object({
      address1: z.string().optional(),
      address2: z.string().optional(),
      city: z.string().optional(),
      postcode: z.string().optional(),
      country: z.nativeEnum(Country).optional(),
      state: z.string().optional(),
    })
    .optional(),
  taxResidencies: z.array(
    z.object({
      country: z.nativeEnum(Country).optional(),
      state: z.string().optional(),
      taxIdentificationNumber: z.string().optional(),
    }),
  ),
  fatcaClassification: z
    .object({
      classification: z.nativeEnum(FatcaClassification),
      tin: z.string().optional(),
      giin: z.string().optional(),
      sponsor: z.string().optional(),
      exemptionDetails: z.string().optional(),
      exemptBeneficialOwnerType: z
        .nativeEnum(FatcaExemptBeneficialOwner)
        .optional(),
      controllingPersonSelfCertificationFiles: z.array(
        z.object({
          path: z.string().min(1, { message: 'File path is required.' }),
          name: z.string().min(1, { message: 'File name is required.' }),
        }),
      ),
      formerUsCitizenFiles: z.array(
        z.object({
          path: z.string().min(1, { message: 'File path is required.' }),
          name: z.string().min(1, { message: 'File name is required.' }),
        }),
      ),
    })
    .optional(),
  crsClassification: z
    .object({
      classification: z.nativeEnum(CrsClassification),
      nonReportingFiOtherDetail: z.string().optional(),
      controllingPersonSelfCertificationFiles: z.array(
        z.object({
          path: z.string().min(1, { message: 'File path is required.' }),
          name: z.string().min(1, { message: 'File name is required.' }),
        }),
      ),
    })
    .optional(),
  declaration: z
    .object({
      fullName: z.string().min(1, { message: 'Full name is required.' }),
      signature: z.string().min(1, { message: 'Signature is required.' }),
      date: z.instanceof(CalendarDay),
      capacity: z.string().optional(),
      files: z.array(
        z.object({
          path: z.string().min(1, { message: 'File path is required' }),
          name: z.string().min(1, { message: 'File name is required' }),
        }),
      ),
    })
    .optional(),
});

export const SaveLegalEntityTaxResidencyDetailsInput = z.object({
  vehicleApplicationId: z.string().refine(isMongoId()),
  principalAddress: z
    .object({
      address1: z.string().optional(),
      address2: z.string().optional(),
      city: z.string().optional(),
      postcode: z.string().optional(),
      country: z.nativeEnum(Country).optional(),
      state: z.string().optional(),
    })
    .nullable(),
  taxResidencies: z.array(
    z.object({
      country: z.nativeEnum(Country).optional(),
      state: z.string().optional(),
      taxIdentificationNumber: z.string().optional(),
    }),
  ),
});

export const SaveLegalEntityFatcaClassificationInput = z.object({
  vehicleApplicationId: z.string().refine(isMongoId()),
  fatcaClassification: z.object({
    classification: z.nativeEnum(FatcaClassification),
    tin: z.string().optional(),
    giin: z.string().optional(),
    sponsor: z.string().optional(),
    exemptionDetails: z.string().optional(),
    exemptBeneficialOwnerType: z
      .nativeEnum(FatcaExemptBeneficialOwner)
      .optional(),
    controllingPersonSelfCertificationFiles: z.array(
      z.object({
        path: z.string().min(1, { message: 'File path is required.' }),
        name: z.string().min(1, { message: 'File name is required.' }),
      }),
    ),
    formerUsCitizenFiles: z.array(
      z.object({
        path: z.string().min(1, { message: 'File path is required.' }),
        name: z.string().min(1, { message: 'File name is required.' }),
      }),
    ),
  }),
});

export const SaveLegalEntityCrsClassificationInput = z.object({
  vehicleApplicationId: z.string().refine(isMongoId()),
  crsClassification: z.object({
    classification: z.nativeEnum(CrsClassification),
    nonReportingFiOtherDetail: z.string().optional(),
    controllingPersonSelfCertificationFiles: z.array(
      z.object({
        path: z.string().min(1, { message: 'File path is required.' }),
        name: z.string().min(1, { message: 'File name is required.' }),
      }),
    ),
  }),
});

export const SaveCustomRequestTaxDetailsInput = z.object({
  vehicleApplicationId: z.string().refine(isMongoId()),
  customRequestAnswers: z
    .array(
      z.object({
        onboardingCustomRequestId: z.string().refine(isMongoId()),
        requestTitle: z
          .string()
          .min(1, { message: 'Request title is required.' }),
        requestDescription: z
          .string()
          .min(1, { message: 'Request description is required.' }),
        answerText: z.string(),
        answerFiles: z.array(
          z.object({
            path: z.string().min(1, { message: 'File path is required.' }),
            name: z.string().min(1, { message: 'File name is required.' }),
          }),
        ),
      }),
    )
    .optional(),
});

export const SaveTaxDeclarationInput = z.object({
  vehicleApplicationId: z.string().refine(isMongoId()),
  markComplete: z.boolean().optional(),
  declaration: z
    .object({
      fullName: z.string().optional(),
      signature: z.string().optional(),
      date: z.instanceof(CalendarDay).optional(),
      capacity: z.string().optional(),
      files: z.array(
        z.object({
          path: z.string().min(1, { message: 'File path is required.' }),
          name: z.string().min(1, { message: 'File name is required.' }),
        }),
      ),
    })
    .optional(),
});

export const SaveCommitmentDetailsInput = z
  .object({
    vehicleApplicationId: z.string().refine(isMongoId()),
    targetCommitment: z.number(),
    baseCurrency: z.nativeEnum(CurrencyCode),
    investorLocalCurrency: z.nativeEnum(CurrencyCode),
    isSubmit: z.boolean().optional(),
  })
  .superRefine((values, context) => {
    if (values.targetCommitment <= 0) {
      context.addIssue({
        path: ['targetCommitment'],
        code: z.ZodIssueCode.custom,
        message: `Your commitment must be greater than ${getCurrencySymbol(
          values.baseCurrency,
        )}0.`,
      });
    }
  });

export const SaveKybDetailsInput = z.object({
  vehicleApplicationId: z.string().refine(isMongoId()),
  markComplete: z.boolean().optional(),
  answers: z.array(
    z.object({
      onboardingCustomRequestId: z.string().refine(isMongoId()).optional(),
      requestKey: z.nativeEnum(KybRequest),
      requestTitle: z.string().min(1, 'Request title is required.'),
      requestDescription: z.string().optional(),
      answerText: z.string().optional(),
      answerFiles: z
        .array(
          z.object({
            path: z.string().min(1, 'File path is required.'),
            name: z.string().min(1, 'File name is required.'),
          }),
        )
        .optional(),
    }),
  ),
});

export const GetCoolingOffPeriodStatusInput = z.object({
  vehicleApplicationId: z.string().refine(isMongoId()),
});

export const SaveKycDetailsInput = z.union([
  z.object({
    vehicleApplicationId: z.string().refine(isMongoId()),
    eKycCompletedAt: z.instanceof(CalendarTime),
    markComplete: z.boolean().optional(),
    answers: z.undefined(),
  }),
  z.object({
    vehicleApplicationId: z.string().refine(isMongoId()),
    markComplete: z.boolean().optional(),
    eKycCompletedAt: z.undefined(),
    answers: z.array(
      z.object({
        onboardingCustomRequestId: z.string().refine(isMongoId()),
        requestKey: z.literal(KycRequest.CustomRequest),
        requestTitle: z.string().min(1, 'Request title is required.'),
        requestDescription: z.string().optional(),
        answerText: z.string().optional(),
        answerFiles: z
          .array(
            z.object({
              path: z.string().min(1, 'File path is required.'),
              name: z.string().min(1, 'File name is required.'),
            }),
          )
          .optional(),
      }),
    ),
  }),
]);
